import { defineComponent } from 'vue';
import BaseComponent from './BaseComponentMixin.jsx';
import utils from '../../Shared/utils.jsx';
import EventBus from '../event-bus.js';
import IconDrawer from './vuecontrols/IconDrawer.vue';
import { buildNavigationDrawerIconItems } from './vuecontrols/navigationDrawerIconItems.js';

const NavigationDrawer = defineComponent({
    name: 'NavigationDrawer',
    extends: BaseComponent,
    components: {
        IconDrawer,
    },
    data() {
        return {
            sourceraw: null,
            sourceurl: null,
            model: null,
            tab_state: {
                index: 0
            },
            mini: true,

            selected_tab: -1,
            selected_tab_top_arr: [0],
            selected_tab_top: 0,
            selected_tab_bot_arr: [],
            selected_tab_bot: -1,
            selected_tab_expr: null,

            selected_popupitem: -1,

            actions: [],
            Javascript: null,

            dense_icons: false,
            collapsed: true,

            bgcolorexpn: null,
            bgimageexpn: null,

            navSelected: [0],
        };
    },
    async created() {
        if (this.controlData.ActiveTabIndex)
            this.selected_tab_expr = utils.compileExpression(this, this.controlData.ActiveTabIndex);

        if (this.controlData.SourceType === 'Raw' && this.controlData.SourceRaw) {
            this.sourceraw = utils.compileObject(this, this.controlData.SourceRaw);
        }
        else if (this.controlData.SourceType === 'Url' && this.controlData.SourceUrl) {
            this.sourceurl = utils.compile(this, this.controlData.SourceUrl);
        }

        if (this.controlData.IconSize)
            this.dense_icons = this.controlData.IconSize == 'Dense';

        EventBus.$on('Action-SelectMenuItem', this.performSelectMenuItem);

        // Add a manual watch to the results of the selected_tab_expr - if it changes, change the selected tab
        this.watcher$ = this.$watch(
            () => {
                try {
                    return utils.evaluate(this.selected_tab_expr, this);
                }
                catch (e) {
                    return null;
                }
            },
            (val, oldval) => {
                let index;
                if (typeof val === 'string')
                    index = parseInt(val);
                else
                    index = val;

                utils.debug(`NavigationDrawer.$watch(index:${index}) (val:${val},oldva:${oldval})`);

                if (index >= this.bot_start_index)
                    this.changeTabBot(index - this.bot_start_index);
                else
                    this.changeTabTop(index);
            }
        );
    },
    unmounted() {
        EventBus.$off('Action-SelectMenuItem', this.performSelectMenuItem);
    },
    computed: {
        totalItems() {
            let total = 0;

            // Each of the tabs in the controls array will be rendered at least once.
            if (Array.isArray(this.model)) {
                total += this.model.length;
            }

            // Add one to include the component itself.
            total++;

            return total;
        },
        ActiveTabIndex() {
            let index;
            if (this.selected_tab_expr)
                index = utils.evaluate(this.selected_tab_expr, this);
            else
                return 0;

            if (typeof index === 'string')
                return parseInt(index);
            else
                return index;
        },
        Menus() {
            return utils.document_cache.menus;
        },

        backgroundColor() {
            if (!this.bgcolorexpn && this.controlData.BackgroundColor)
                this.bgcolorexpn = utils.compile(this, this.controlData.BackgroundColor);

            return this.bgcolorexpn ? utils.evaluate(this.bgcolorexpn, this) : undefined;
        },
        backgroundImage() {
            if (!this.bgimageexpn && this.controlData.BackgroundImage)
                this.bgimageexpn = utils.compile(this, this.controlData.BackgroundImage);

            return this.bgimageexpn ? utils.evaluate(this.bgimageexpn, this) : undefined;
        },

        bot_start_index() {
            if (!this.model || !Array.isArray(this.model))
                return 0;
            return this.model.filter((t) => t.location === 'Top').length;
        },

        iconDrawerItems() {
            return buildNavigationDrawerIconItems({
                model: this.model,
                collapsed: this.collapsed,
                translate: (k) => this.Translate(k),
                generateUUID: () => utils.generateUUID(),
                onCollapseToggle: () => { this.collapsed = !this.collapsed; },
            });
        },
    },
    methods: {
        async preRenderComplete() {
            await this.Refresh();
            this.finishRenderHandler(this);
        },
        async performSelectMenuItem(action) {
            utils.log(`NavigationDrawer SelectMenuItem ${action.ActionData.MenuItemName}`);

            if (action.ActionData.Debug && action.ActionData.Debug.BreakPoint) debugger;

            try {
                const name = utils.evaluate(action.menuname, action.context);

                utils.executeAndCompileAllActions(this.controlData.SelectMenuActions, { Data: name }, this);

                // Create a delayed action to signal the menu item since the menu may not be materialized yet. By waiting,
                // we allow the menu to load and register for the SelectMenuItem-Menu event.
                setTimeout(() => { EventBus.$emit('Action-SelectMenuItem-Menu', action); }, 250);

                try {
                    await utils.success(action);
                } catch (e) { }
            }
            catch (e) {
                try {
                    await utils.failure(action, { Data: e });
                } catch (e) { }
            }
            finally {
                try {
                    await utils.complete(action);
                }
                catch (e) { }

                // Complete the promise for the executeAction method
                action.FinishFunc(true);
            }
        },

        async Refresh() {
            if (this.sourceraw)
                try {
                    this.model = utils.evaluateObject(this.sourceraw, this);
                }
                catch (e) {
                    utils.warn('DynamicTab failed to evaluate SourceRaw ' + this.controlData.SourceRaw + ': ' + e);
                }
            else if (this.sourceurl)
                try {
                    this.model = await utils.api.get(utils.evaluate(this.sourceurl, this));
                }
                catch (e) {
                    utils.warn('DynamicTab failed to evaluate SourceUrl ' + this.controlData.SourceUrl + ': ' + e);
                }

        },
        async changeTab(index) {
            utils.debug(`NavigationDrawer.changeTab(index:${index}) - firing Actions`);

            let runActions = true;
            let i = index;

            if (isNaN(i))
                i = this.selected_tab;

            if (i > -1 && this.model && this.model.length > i) {
                var menuItem = this.model[i];

                if (menuItem?.kind && (menuItem.kind == "PopupMenu" || menuItem.kind == "ActionsOnly")) {
                    runActions = false; //do not run actions for popup menus or action only menus
                }
            }

            // Perform Actions that will actually change the visible static tab
            if (runActions)
                utils.executeAndCompileAllActions(this.controlData.Actions, { Data: index }, this);

            // index of selected tab
            this.selected_tab = index;
        },
        changeTabTop(index) {
            //const index = indexarr && indexarr.length ? indexarr[0] : -1;
            utils.debug(`NavigationDrawer.changeTabTop(index:${index})`);

            if (!isNaN(index) && index > -1 && this.model && this.model.length > index) {
                const menuItem = this.model[index];

                if (menuItem?.kind && (menuItem.kind == "PopupMenu" || menuItem.kind == "ActionsOnly")) {
                    const old_tab = this.selected_tab_top;
                    this.selected_tab_top = index;
                    this.$nextTick(() => this.selected_tab_top = old_tab);
                    return;
                }
            }

            //this.selected_tab_top_arr = indexarr;
            this.selected_tab_top = index;
            //this.selected_tab_bot_arr = [];
            this.selected_tab_bot = -1;

            if (this.controlData.UseRouter) {
                if (this.model[index] && this.model[index].type != 'OpenTabs' && 'RouterCurrentPath' in this && !this.RouterCurrentPath.endsWith(`#/${this.controlData.RoutingName}/${this.model[index].$objectid}`))
                    this.RouterPush(`#/${this.controlData.RoutingName}/${this.model[index].$objectid}`);
                else
                    this.changeTab(index);
            }
            else
                this.changeTab(index);
        },
        changeTabBot(index) {
            //const index = indexarr && indexarr.length ? indexarr[0] : -1;
            utils.debug(`NavigationDrawer.changeTabBot(index:${index}) this.selected_tab_bot:${this.selected_tab_bot}`);

            if (!isNaN(index) && (index + this.bot_start_index) > -1 && this.model && this.model.length > (index + this.bot_start_index)) {
                const menuItem = this.model[index + this.bot_start_index];

                if (menuItem?.kind && (menuItem.kind == "PopupMenu" || menuItem.kind == "ActionsOnly")) {
                    const old_tab = this.selected_tab_bot;
                    this.selected_tab_bot = index;
                    this.$nextTick(() => this.selected_tab_bot = old_tab);
                    return;
                }
            }

            //this.selected_tab_top_arr = [];
            this.selected_tab_top = -1;
            //this.selected_tab_bot_arr = indexarr;
            this.selected_tab_bot = index;

            if (this.controlData.UseRouter) {
                if (this.model[index + this.bot_start_index] && this.model[index + this.bot_start_index].type != 'OpenTabs' && 'RouterCurrentPath' in this && !this.RouterCurrentPath.endsWith(`#/${this.controlData.RoutingName}/${this.model[index + this.bot_start_index].$objectid}`))
                    this.RouterPush(`#/${this.controlData.RoutingName}/${this.model[index + this.bot_start_index].$objectid}`);
                else
                    this.changeTab(index + this.bot_start_index);
            }
            else
                this.changeTab(index + this.bot_start_index);
        },
        selectPopupMenuItem(e, menu) {
            //e.cancelBubble = true;
            //e.stopPropagation();
            //alert('Popup Menu ' + menu.Title);
            //this.selected_popupitem = -1;
            //debugger;

            if (menu.Data && menu.Data.MenuItemData)
                utils.executeAndCompileAllActions(menu.Data && menu.Data.MenuItemData.Actions, null, this);
        },
        SetActiveTabIndex(index) {
            utils.debug(`NavigationDrawer.SetActiveTabIndex(index:${index}) note selected_tab:${this.selected_tab}`);

            if (index !== this.selected_tab)
                this.changeTab(index);
        },

        onIconDrawerSelect(id) {
            this.SetActiveTabIndex(id);
        },

        onIconDrawerMenuAction({ menuItem, handled }) {
            if (handled)
                return;

            const actionData = menuItem?._navChild?.Data?.MenuItemData?.Actions
                ?? menuItem?.Data?.MenuItemData?.Actions;
            if (actionData)
                utils.executeAndCompileAllActions(actionData, null, this);
        },

        onIconDrawerAction({ item, handled }) {
            if (handled)
                return;
            if (item._navAction) {
                item._navAction();
                return;
            }
            if (item._navTab?.Actions)
                utils.executeAndCompileAllActions(item._navTab.Actions, null, this);
        },
    },
    watch: {
        //selected_tab: function (newval, oldval) {
        //    utils.debug(`NavigationDrawer.selected_tab changed from ${oldval} to ${newval}`);
        //},
        //selected_tab_bot: function(newval, oldval) {
        //    utils.debug(`NavigationDrawer.selected_tab_bot changed from ${oldval} to ${newval}`);
        //},
        RouterCurrentPath: function (newVal, oldVal) {
            if (this.controlData.UseRouter) {
                //need to check for /Tab/guid and set the active index to open tabs if necessary
                var rgx = new RegExp(`${this.controlData.RoutingName}/([^/]*)`, 'ig');
                var matches = [...newVal.matchAll(rgx)];

                if (matches[0] && matches[0].length >= 2) {
                    utils.debug(`Navigation newVal: ${newVal}; oldVal: ${oldVal};`);
                    var index = this.model.findIndex(o => o.$objectid == matches[0][1]);
                    this.changeTab(index);
                }
            }
        }
    },
    props: {
    },
    render() {
        if (!this.todisplay || !this.controlData)
            return null;

        const useDark = this.controlData.DarkTheme || false;
        const drawerWidth = this.collapsed ? 56 : 256; 
        const wrapperStyle = {
            ...this.sizeStyle,
            zIndex: 2000,
            width: `${drawerWidth}px`,
            minWidth: `${drawerWidth}px`,
            maxWidth: `${drawerWidth}px`,
            flexShrink: 0,
            overflow: 'hidden',
            transition: 'width 0.25s ease, min-width 0.25s ease, max-width 0.25s ease',
        };

        return (
            <div
                id="vmenuNavAnchor"
                class={['c-NavigationDrawer', 'c-NavigationDrawer--standalone', `c-name-${this.name || 'unnamed'}`]}
                style={wrapperStyle}
            >
                <IconDrawer
                    items={this.iconDrawerItems}
                    modelValue={this.selected_tab}
                    onUpdate:modelValue={(v) => this.SetActiveTabIndex(v)}
                    onSelect={this.onIconDrawerSelect}
                    onMenu-action={this.onIconDrawerMenuAction}
                    onAction={this.onIconDrawerAction}
                    onItem-finished-render={(reference) => this.finishRenderHandler(reference)}
                    width={drawerWidth}
                    expanded={!this.collapsed}
                    dense={this.dense_icons}
                    dark={useDark}
                    backgroundColor={this.backgroundColor}
                    root={this.root}
                    scopeitems={this.scopeitems}
                    controlscope={this.controlscope}
                    namePrefix={this.name}
                />
            </div>
        );
    }
});

export default NavigationDrawer;